import React from 'react';
import { LegalSectionBody } from '../components/legal/LegalSectionBody';

export const SAFETY_SUMMARY_CARDS = [
  {
    icon: '📵',
    title: 'Screenshot Protection',
    desc: 'We actively discourage and restrict screenshots — because private queer chats should stay private.',
  },
  {
    icon: '✓',
    title: 'Public & Private Verification',
    desc: 'Choose visible trust or discreet verification. Privacy should never mean less safety.',
  },
  {
    icon: '🚪',
    title: 'Closeted User Safety',
    desc: 'Not everyone can be out publicly. We built StrapIn with discretion in mind.',
  },
  {
    icon: '🚨',
    title: 'Reporting & Blocking',
    desc: 'Report harassment, scams, outing threats, and more — reviewed by real humans.',
  },
  {
    icon: '🛡️',
    title: 'Scam Prevention',
    desc: 'Verification, moderation, and fraud signals — without pretending perfection exists.',
  },
  {
    icon: '💜',
    title: 'Human Moderation',
    desc: 'Structured reports with context. Serious issues get serious review.',
  },
];

function section(title, icon, bodyProps) {
  return {
    title,
    icon,
    content: <LegalSectionBody {...bodyProps} />,
  };
}

export const SAFETY_SECTIONS = [
  section('Why StrapIn Exists', '🌈', {
    intro:
      'Most dating apps were not designed around queer realities — especially privacy, closeted life, and outing risk.',
    paragraphs: [
      'StrapIn was built because connection should not cost your safety, your closet, or your peace of mind.',
      'We understand screenshot anxiety, fake profiles, scams, blackmail fears, and the weight of meeting someone new when being out is not an option for everyone.',
      'Queer safety is not a marketing line here. It is the reason the product exists.',
    ],
  }),
  section('Screenshot Protection', '📵', {
    intro:
      'Private conversations — especially for queer and closeted users — can carry real-world consequences if shared.',
    paragraphs: [
      'StrapIn actively discourages and restricts screenshots and screen recordings within the app.',
      'No app can block every capture method on every device. External cameras, certain OS limitations, or bypass attempts may still be possible.',
      'Recording, capturing, or sharing private chats or media without consent violates our rules and may result in permanent removal.',
      'If someone shares your private content without consent, report them immediately and consider seeking support from people you trust.',
    ],
  }),
  section('Public vs Private Verification', '✓', {
    intro:
      'Trust and privacy should not be opposites. That is why StrapIn offers two verification paths.',
    subsections: [
      {
        heading: 'Public verification',
        paragraphs: [
          'For users comfortable displaying a verification badge. It helps others know your profile went through identity review — not that we guarantee who someone is in every future moment.',
        ],
      },
      {
        heading: 'Private / discreet verification',
        paragraphs: [
          'For users who need stronger internal trust without public visibility. Your verification media is never shown to other users — only reviewed by authorized moderation staff.',
        ],
      },
      {
        heading: 'What you may be asked to submit',
        bullets: [
          'A clear selfie',
          'An angled selfie',
          'A short liveness video',
        ],
      },
    ],
    paragraphs: [
      'Privacy should never mean less safety. Verification helps reduce impersonation — it does not replace your judgment.',
    ],
  }),
  section('Closeted User Safety', '🚪', {
    intro:
      'Not everyone can safely be publicly visible. We see you — and we built with that in mind.',
    bullets: [
      'Control what you share on your profile and in chats',
      'Use private verification if you want trust without a public badge',
      'Be cautious sharing identifiable details (workplace, address, full name) early on',
      'Use block and report if someone pressures you to come out or threatens exposure',
      'Remember: you never owe anyone your story on their timeline',
    ],
    paragraphs: [
      'Outing threats and blackmail are serious violations. Report them. We investigate.',
    ],
  }),
  section('Reporting & Blocking', '🚨', {
    intro:
      'You are not alone when something goes wrong. StrapIn gives you tools to protect yourself quickly.',
    subsections: [
      {
        heading: 'You can report',
        bullets: [
          'Harassment or bullying',
          'Scams or financial fraud',
          'Impersonation or fake profiles',
          'Blackmail or coercion',
          'Outing threats or privacy violations',
          'Non-consensual content sharing',
          'Threats or unsafe behavior',
        ],
      },
      {
        heading: 'What happens next',
        bullets: [
          'Reports go to our moderation team with the category and context you provide',
          'You can block someone to stop contact',
          'Serious cases may lead to immediate restrictions or permanent bans',
        ],
      },
    ],
  }),
  section('Scam & Fake Profile Prevention', '🎭', {
    intro:
      'Bad actors exist on every platform. We work to reduce them — honestly, not with empty promises.',
    bullets: [
      'Identity verification (public and private) to reduce impersonation',
      'Human moderation review of structured safety reports',
      'Fraud signals and repeat-offender detection',
      'Account restrictions and bans for policy violations',
    ],
    paragraphs: [
      'Stay alert: be wary of rushed intimacy, money requests, off-platform payment links, and profiles that refuse reasonable verification. Trust your instincts.',
    ],
  }),
  section('Harassment, Consent & Outing', '💜', {
    intro:
      'Respect is not optional here. Consent is ongoing — in messages, media, and meetups.',
    bullets: [
      'No coercion, threats, or pressure',
      'No outing — ever',
      'No blackmail using photos, chats, or closeted status',
      'No hate speech or targeted abuse',
      'Stop when someone says no — online or offline',
    ],
    paragraphs: [
      'Queer spaces should feel safer. Help us protect that by reporting harm and treating others with dignity.',
    ],
  }),
  section('Safer Meetup Tips', '📍', {
    intro:
      'Meeting in person is your choice. StrapIn cannot guarantee offline safety — but these practices help:',
    bullets: [
      'Meet in a public place the first time',
      'Tell a trusted friend where you are going and who you are meeting',
      'Arrange your own transportation',
      'Keep your phone charged and accessible',
      'Do not feel pressured to share more than you are comfortable with',
      'Leave immediately if something feels wrong — you do not owe anyone an explanation',
      'Trust your gut. If it feels off, it probably is.',
    ],
  }),
  section('Privacy Philosophy', '🔒', {
    intro: 'Privacy is not a premium upsell. It is the foundation.',
    bullets: [
      'We do not sell your personal data',
      'We design features around queer privacy realities — not generic dating-app defaults',
      'Screenshot discouragement, verification privacy, and structured reporting work together',
    ],
    paragraphs: [
      'Read our Privacy Policy for details on data handling. Read our Terms for community rules.',
    ],
  }),
  section('Emergency & Support', '🆘', {
    intro:
      'If you are in immediate danger, contact local emergency services first.',
    bullets: [
      'In-app: report and block from the profile or chat menu',
      'Safety concerns: safety@strapinapp.com',
      'General support: support@strapinapp.com',
      'Privacy requests: privacy@strapinapp.com',
    ],
    paragraphs: [
      'If you are experiencing a mental health crisis, reach out to a trusted person or a local crisis line in your country. You deserve support.',
    ],
  }),
];
